import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Suspense } from "react";
import { Pages } from "@/lib/lazyPages";
import { Loader2 } from "lucide-react";

const {
  Login, ForgotPassword, ResetPassword, AuditLog, Dashboard, Etablissements, EtablissementDetail,
  Sites, Locaux, Equipements, EquipementPassport, Autorisations, DossierArsn, Referentiel, Checkups,
  NonConformites, PlanAction, Documents, ServicesCEP, Prospects, Opportunites, Pipeline, Utilisateurs,
  Parametres, Profil, Travailleurs, TravailleurDetail,
} = Pages;

const TRAV_ROLES = ["admin_cep", "expert_cep", "client_admin"];

const FullScreenLoader = () => (
  <div data-testid="page-loader-fullscreen" className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== false) return <Navigate to="/" replace />;
  return children;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<FullScreenLoader />}>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invitation" element={<ResetPassword invite />} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="etablissements" element={<Etablissements />} />
              <Route path="etablissements/:id" element={<EtablissementDetail />} />
              <Route path="sites" element={<Sites />} />
              <Route path="locaux" element={<Locaux />} />
              <Route path="equipements" element={<Equipements />} />
              <Route path="equipements/:id" element={<EquipementPassport />} />
              <Route path="autorisations" element={<Autorisations />} />
              <Route path="autorisations/:id/dossier" element={<DossierArsn />} />
              <Route path="travailleurs" element={<RequireRole roles={TRAV_ROLES}><Travailleurs /></RequireRole>} />
              <Route path="travailleurs/:id" element={<RequireRole roles={TRAV_ROLES}><TravailleurDetail /></RequireRole>} />
              <Route path="referentiel" element={<RequireRole roles={["admin_cep"]}><Referentiel /></RequireRole>} />
              <Route path="checkup" element={<Checkups />} />
              <Route path="non-conformites" element={<NonConformites />} />
              <Route path="plan-action" element={<PlanAction />} />
              <Route path="documents" element={<Documents />} />
              <Route path="services" element={<ServicesCEP />} />
              <Route path="prospects" element={<RequireRole roles={["admin_cep", "expert_cep"]}><Prospects /></RequireRole>} />
              <Route path="opportunites" element={<RequireRole roles={["admin_cep", "expert_cep"]}><Opportunites /></RequireRole>} />
              <Route path="pipeline" element={<RequireRole roles={["admin_cep", "expert_cep"]}><Pipeline /></RequireRole>} />
              <Route path="utilisateurs" element={<RequireRole roles={["admin_cep", "expert_cep", "client_admin"]}><Utilisateurs /></RequireRole>} />
              <Route path="journal-audit" element={<RequireRole roles={["admin_cep"]}><AuditLog /></RequireRole>} />
              <Route path="parametres" element={<Parametres />} />
              <Route path="profil" element={<Profil />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
